import { LayoutWrapper } from "@/components/LayoutWrapper";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Phone, Thermometer, FileWarning, Shield, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

const EmergencyActions = () => {
  const navigate = useNavigate();

  const emergencyContacts = [
    { name: "Emergency Services", number: "999", icon: Phone, urgent: true },
    { name: "Food Standards Agency", number: "0330 332 7149", icon: Shield, urgent: false },
    { name: "Health & Safety Executive", number: "0300 003 1647", icon: AlertCircle, urgent: false },
  ];

  return (
    <LayoutWrapper>
      <div className="container mx-auto p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">🚨 Emergency Actions</h1>
            <p className="text-muted-foreground mt-2">Quick access to critical incident reporting and emergency contacts</p>
          </div>
        </div>

        {/* Critical Actions */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card className="border-red-500 border-2 hover:shadow-lg transition-shadow bg-red-500/5">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <CardTitle>Report Critical Incident</CardTitle>
              </div>
              <CardDescription>
                Food safety violation, contamination, or customer illness
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/incidents')}
                className="w-full bg-red-500 hover:bg-red-600 text-white"
                size="lg"
              >
                Report Now
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Thermometer className="h-6 w-6 text-orange-500" />
                <CardTitle>Temperature Alert</CardTitle>
              </div>
              <CardDescription>
                Log equipment failure or out-of-range temperatures
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/checklists/new')}
                className="w-full"
                variant="outline"
                size="lg"
              >
                Log Temperature Issue
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileWarning className="h-6 w-6 text-amber-500" />
                <CardTitle>Corrective Action</CardTitle>
              </div>
              <CardDescription>
                Document immediate corrective measures taken
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/incidents')}
                className="w-full"
                variant="outline"
                size="lg"
              >
                Log Action Taken
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Emergency Contacts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Emergency Contacts
            </CardTitle>
            <CardDescription>
              Critical contact numbers for food safety emergencies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {emergencyContacts.map((contact) => (
              <div 
                key={contact.name}
                className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <contact.icon className={`h-5 w-5 ${contact.urgent ? 'text-red-500' : 'text-primary'}`} />
                  <div>
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-muted-foreground">Available 24/7</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {contact.urgent && (
                    <Badge variant="destructive" className="text-xs">Urgent</Badge>
                  )}
                  <a 
                    href={`tel:${contact.number}`}
                    className="text-lg font-mono font-semibold hover:underline"
                  >
                    {contact.number}
                  </a>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Quick Reference */}
        <Card>
          <CardHeader>
            <CardTitle>When to Use Emergency Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="font-semibold text-red-500 mb-2">🔴 Critical - Report Immediately:</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Customer illness or allergic reaction</li>
                  <li>• Food contamination or foreign objects</li>
                  <li>• Major equipment failure (fridge/freezer)</li>
                  <li>• Pest infestation discovered</li>
                  <li>• Water supply contamination</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-amber-500 mb-2">🟡 Urgent - Log Within 1 Hour:</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Temperature out of safe range</li>
                  <li>• Staff illness related to food handling</li>
                  <li>• Cleaning chemical spill</li>
                  <li>• Cross-contamination incident</li>
                  <li>• Delivery temperature non-compliance</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </LayoutWrapper>
  );
};

export default EmergencyActions;
